import React, { useState, useCallback, memo, useEffect } from 'react'
import classes from './Settings.module.sass'
import { ExecutorClusterResponse } from '../../../../../../common/types/interfaces'
import { Toggle } from '../../../../../../common/components/newToggle/Toggle'
import { getSalesApi } from '../../../../../../salesApi/salesApi'

type SettingsData = { isOnShift: boolean; notifyNewTasks: boolean }

type Props = {
  cluster: ExecutorClusterResponse | null
  settings: SettingsData | null
  onUpdated?: (settings: SettingsData) => void
}

type FieldKey = 'isOnShift' | 'notifyNewTasks'

const SettingsRow = memo(
  ({
    label,
    hint,
    checked,
    disabled,
    onToggle,
  }: {
    label: string
    hint: string
    checked: boolean
    disabled: boolean
    onToggle: (v: boolean) => void
  }) => (
    <div className={classes.row}>
      <div className={classes.labelBlock}>
        <div className={classes.label}>{label}</div>
        <div className={classes.hint}>{hint}</div>
      </div>
      <Toggle checked={checked} onChange={onToggle} disabled={disabled} />
    </div>
  ),
)
SettingsRow.displayName = 'SettingsRow'

export const Settings: React.FC<Props> = ({ cluster, settings, onUpdated }) => {
  const [isOnShift, setIsOnShift] = useState(settings?.isOnShift ?? false)
  const [notifyNewTasks, setNotifyNewTasks] = useState(settings?.notifyNewTasks ?? false)
  const [savingField, setSavingField] = useState<FieldKey | null>(null)

  const api = getSalesApi()

  // ❌ если настройки обновились снаружи
  useEffect(() => {
    if (settings) {
      setIsOnShift(settings.isOnShift)
      setNotifyNewTasks(settings.notifyNewTasks)
    }
  }, [settings])

  const handleToggle = useCallback(
    async (field: FieldKey, value: boolean) => {
      if (!cluster) return
      setSavingField(field)
      try {
        api.setClusterUrl(cluster.cluster)
        const updated = await api.updateExecutorSettings({ [field]: value })
        if (field === 'isOnShift') setIsOnShift(value)
        if (field === 'notifyNewTasks') setNotifyNewTasks(value)
        onUpdated?.(updated)
      } catch (error) {
        console.error('Failed to update executor settings:', error)
      } finally {
        setSavingField(null)
      }
    },
    [cluster, api, onUpdated],
  )

  const handleIsOnShift = useCallback((v: boolean) => handleToggle('isOnShift', v), [handleToggle])
  const handleNotify = useCallback(
    (v: boolean) => handleToggle('notifyNewTasks', v),
    [handleToggle],
  )

  if (!cluster || !settings) return null

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.title}>Настройки исполнителя</div>
        <SettingsRow
          label="На смене"
          hint="Показывать доступные задания"
          checked={isOnShift}
          disabled={savingField === 'isOnShift'}
          onToggle={handleIsOnShift}
        />
        <SettingsRow
          label="Уведомления о новых заданиях"
          hint="Присылать пуши о новых заданиях"
          checked={notifyNewTasks}
          disabled={savingField === 'notifyNewTasks'}
          onToggle={handleNotify}
        />
      </div>
    </div>
  )
}
